/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @version 1.11.0
 * @since 1.0.1
 */

import { Fragment, memo, useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../../../assets';
import { Button } from '../../../../../../components/common';
import { FlexColumnStyled, FlexMiddleStyled } from '../../../../../../components/common/Block/Flexbox';
import { ImgStyled } from '../../../../../../components/common/Block/Img';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { ELinkTarget } from '../../../../../../components/common/ExternalLink/ExternalLink';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import LinkBlockServiceUserDenied from '../../../../../../modules/role/components/LinkBlockServiceUserDenied';
import { AccessComponentKeyEnum } from '../../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../../../../../modules/role/selectors/guardSelector';
import { setCookie } from '../../../../../../modules/storage/storageActions';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { ECookieKey, ISetCookiePayload } from '../../../../../../modules/storage/storageTypes';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { openExternalRoute } from '../../../../../../routes/constants/externalRoutes';
import { WidgetHelpTextStyled, WidgetHelpTitleStyled } from '../../styled/WidgetHelpStyled';
import { WidgetEmptyCircleStyled, WidgetTitleStyled } from '../../styled/WidgetStyled';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { WidgetVotingContainerStyled } from '../styled/WidgetVoitinStyled';
import { widgetVotingLoadSurvey } from '../widgetVotingActions';
import {
    makeSelectorWidgetVotingAnonymous,
    makeSelectorWidgetVotingUrl,
    makeSelectorWidgetVotingUserId,
} from '../widgetVotingSelectors';

interface IConnectedProps {
    anonymous: boolean;
    isMedalliaEnabled: boolean;
    selectedAccount: IWidgetSearchAccountsItem;
    url: string;
    userId: number;
}

interface IDispatchProps {
    loadSurvey: () => void;
    setCookie: (payload: ISetCookiePayload) => void;
}

interface IWidgetVotingProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetVoting = IConnectedProps & IDispatchProps & IWidgetVotingProps;

export const WidgetVoting = (props: TWidgetVoting) => {
    const translation = props.translation.widgets.voting;
    const {
        anonymous,
        isMedalliaEnabled,
        loadSurvey,
        selectedAccount,
        setCookie,
        url,
        userId,
    } = props;

    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (isMedalliaEnabled) return;

        loadSurvey();
    }, [
        isMedalliaEnabled,
        loadSurvey,
        selectedAccount.id,
    ]);

    const getBtnUrl = useCallback((): string =>
            anonymous ? url : `${url}?id=${userId}`,
        [
            anonymous,
            url,
            userId,
        ],
    );

    const onBtnClick = useCallback(() => {
        setCookie({
            key: ECookieKey.VOTING_HIDE,
            value: url,
        });
        setIsVisible(false);
        openExternalRoute(getBtnUrl(), ELinkTarget.BLANK);
    }, [
        getBtnUrl,
        setCookie,
        url,
    ]);


    if (!url || !isVisible || isMedalliaEnabled) {
        return null;
    }
    return (
        <Fragment>
            {
                isVisible &&
                <WidgetVotingContainerStyled
                    data-widget='WidgetVoting'
                >
                    <FlexColumnStyled>
                        <LayoutStyled className="mb-4">
                            <WidgetTitleStyled>
                                {translation.widgetTitle}
                            </WidgetTitleStyled>
                        </LayoutStyled>
                        <FlexMiddleStyled>
                            <WidgetEmptyCircleStyled>
                                <ImgStyled
                                    className="medium"
                                    src={icons.messageIconMedium}/>
                            </WidgetEmptyCircleStyled>
                        </FlexMiddleStyled>
                        <LayoutStyled className="mb-1 mt-3 t-center">
                            <WidgetHelpTitleStyled>
                                {translation.title}
                            </WidgetHelpTitleStyled>
                        </LayoutStyled>

                        <LayoutStyled className="mb-4 t-center">
                            <WidgetHelpTextStyled>
                                <div dangerouslySetInnerHTML={({ __html: translation.text })}/>
                            </WidgetHelpTextStyled>
                        </LayoutStyled>

                        <LinkBlockServiceUserDenied
                            data-link="getBtnUrl"
                            isExternalUrl={true}
                            renderChild={(disabled: boolean) => <Button
                                className="widget-voting__btn"
                                dataName="widgetVotingButton"
                                disabled={disabled}
                                kind="mediumNormal"
                            >
                                {translation.btn}
                            </Button>}
                            url={getBtnUrl()}
                            onClick={onBtnClick}
                            onWheelClick={onBtnClick}
                        />

                    </FlexColumnStyled>
                </WidgetVotingContainerStyled>
            }
        </Fragment>
    );
};

WidgetVoting.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    anonymous: makeSelectorWidgetVotingAnonymous(),
    isMedalliaEnabled: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.surveyMedallia),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    url: makeSelectorWidgetVotingUrl(),
    userId: makeSelectorWidgetVotingUserId(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadSurvey: () => dispatch(widgetVotingLoadSurvey()),
    setCookie: (payload: ISetCookiePayload) => dispatch(setCookie(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetVoting));
